// Recurring schedule / availability slot times.
//
// Pickup schedules and courier availability store slot times as Pacific
// wall-clock "HH:MM:SS" strings (no "Z"), keyed by Pacific day of week —
// "18:30:00" under "monday" means Monday 6:30 PM Pacific, all year round.
// The backend's SchedulingTime (app/services/scheduling_time.rb) uses the
// same zone. Never convert these through the browser's local zone: every
// partner operates in Pacific time, whatever machine they're using.

export const SCHEDULE_TIME_ZONE = "America/Los_Angeles";
export const SCHEDULE_TIME_ZONE_LABEL = "Pacific Time";

/** "18:30:00" → "18:30", for an <input type="time"> value. */
export function slotToInput(slotTime: string): string {
  return slotTime.slice(0, 5);
}

/** "18:30" (from an <input type="time">) → "18:30:00" for the API. */
export function inputToSlot(inputTime: string): string {
  return inputTime.length === 5 ? `${inputTime}:00` : inputTime;
}

/** "18:30:00" → "6:30 PM PT". Pure string formatting — no zone conversion. */
export function formatSlotTime(slotTime?: string): string {
  if (!slotTime) return "";
  const [h, m] = slotTime.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return slotTime;
  const period = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period} PT`;
}

// Minutes the schedule zone is ahead of UTC at `instant` (e.g. -420 for PDT).
function zoneOffsetMinutes(instant: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: SCHEDULE_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return Math.round((asUtc - instant.getTime()) / 60000);
}

/**
 * The instant for a Pacific calendar date ("yyyy-MM-dd") and slot time
 * ("HH:MM:SS"), with the correct PDT/PST offset for that date. Render the
 * result with normal Date methods to show it in the viewer's zone.
 */
export function pacificDateTime(dateStr: string, slotTime: string): Date {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi, s = 0] = slotTime.split(":").map(Number);
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s);

  // Two passes handle dates where the offset changes (DST transitions).
  let offset = zoneOffsetMinutes(new Date(wallAsUtc));
  const secondOffset = zoneOffsetMinutes(new Date(wallAsUtc - offset * 60000));
  if (secondOffset !== offset) offset = secondOffset;

  return new Date(wallAsUtc - offset * 60000);
}
